import { Component, OnInit, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { NgxUiLoaderService, SPINNER } from 'ngx-ui-loader';
import Swal from 'sweetalert2';
import { MapsAPILoader } from '@agm/core';
import moment from 'moment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

declare const google: any;

@Component({
  selector: 'app-housing-hub',
  templateUrl: './housing-hub.component.html',
  styleUrls: ['./housing-hub.component.css']
})
export class HousingHubComponent implements OnInit {
  form: FormGroup;
  submitted = false;
  spinnerType = SPINNER.circle;
  defaultBounds: any;
  boundsLimit = true;
  hasGoogleSelection = false;
  locationData: any = {};
  housingPosts: any[] = [];
  showCreateForm = false;
  pageSize: number = 6;
  page: number = 1;
  selectedPost: any = null;
  isEditMode = false;
  editingPostId: string | null = null;

  // Options
  sellStatusOptions = ['Sale', 'Rent', 'Purchase'];
  propertyTypeOptions = [
    'Apartment/Flat', 'House', 'Condo', 'Townhouse', 'Duplex',
    'Guest House', 'Manufactured', 'Assisted Living', 'Farm/Ranch', 'Land', 'Commercial'
  ];
  housingTypeOptions = ['Private', 'Shared'];
  petOptions = ['No', 'Dogs', 'Cats', 'Birds', 'Other'];
  statusOptions = ['Active', 'Cancelled', 'Sold', 'Inactive', 'Rented', 'Purchased'];
  propertyUnitOptions = ['sq ft', 'sq m', 'acres'];
  lotUnitOptions = ['sq ft', 'sq m', 'acres'];

  // Additional Information Options
  parkingOptions = ['Yes', 'No', 'Garage', 'Street', 'Covered'];
  laundryOptions = ['Yes', 'No', 'In Unit', 'On Site', 'Hookups'];
  flooringOptions = ['Carpet', 'Hardwood', 'Tile', 'Vinyl', 'Laminate', 'Other'];
  furnishedOptions = ['Yes', 'No', 'Partially'];
  smokingOptions = ['Yes', 'No', 'Outside Only'];
  amenitiesOptions = ['Pool', 'Gym', 'Parking', 'Security', 'Elevator', 'Balcony', 'Garden', 'Other'];
  evChargingOptions = ['Yes', 'No', 'Available'];
  wheelchairOptions = ['Yes', 'No', 'Partial'];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authenticationService: AuthenticationService,
    private ngxService: NgxUiLoaderService,
    private mapsAPILoader: MapsAPILoader,
    private zone: NgZone,
    private modalService: NgbModal
  ) {
    this.getLocation();
  }

  ngOnInit(): void {
    this.getHousingPosts();
    this.initializeForm();
  }

  initializeForm() {
    this.form = this.fb.group({
      sellStatus: ['Sale', Validators.required],
      propertyType: ['Apartment/Flat', Validators.required],
      housingType: [''],
      roomTitle: ['', Validators.required],
      bedRoomCount: [0],
      washRoomCount: [0],
      propertyArea: [0],
      propertyUnitArea: ['sq ft'],
      lotLandArea: [0],
      lotUnitArea: ['sq ft'],
      price: [''],
      monthlyRent: [0],
      weeklyRent: [0],
      duration: [0],
      availableDate: [new Date()],
      rentalPeriod: [''],
      openHouseInfo: [''],
      description: [''],
      contactNumber: ['', Validators.required],
      contactInstruction: [''],
      email: ['', [Validators.email]],
      name: [''],
      ravelId: [''],
      pets: this.fb.array([]),
      status: ['Active'],
      isAvailable: [true],
      isShowProfile: [false],
      isFavorite: [false],
      defaultImage: [-1],
      images: this.fb.array([]),
      address: this.fb.group({
        googleAddress: [''],
        streetAddress: [''],
        landMark: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: [''],
        contactNo: [''],
        alternateNo: [''],
        location: this.fb.group({
          type: ['Point'],
          coordinates: [[0, 0]]
        })
      }),
      city: [''],
      postalCode: [''],
      additionalInformation: this.fb.group({
        Parking: this.fb.array([]),
        Laundry: this.fb.array([]),
        Flooring: this.fb.array([]),
        Furnished: this.fb.array([]),
        Smoking: this.fb.array([]),
        Amenities: this.fb.array([]),
        EVCharging: this.fb.array([]),
        WheelchairAccessible: this.fb.array([])
      })
    });
  }

  getLocation() {
    this.mapsAPILoader.load().then(() => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.defaultBounds = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
        });
      } else {
        this.defaultBounds = { lat: 0, lng: 0 };
      }
    });
  }

  onLocationChange(addressData: any) {
    this.zone.run(() => {
      this.hasGoogleSelection = true;
      this.locationData = addressData;
      
      const addressGroup = this.form.get('address') as FormGroup;
      if (addressGroup) {
        addressGroup.patchValue({
          googleAddress: addressData.googleAddress || '',
          streetAddress: addressData.streetAddress || '',
          landMark: addressData.landMark || '',
          city: addressData.city || '',
          state: addressData.state || '',
          postalCode: addressData.postalCode || '',
          country: addressData.country || '',
          location: {
            type: 'Point',
            coordinates: addressData.location?.coordinates || [0, 0]
          }
        });
      }
      
      // Also update city and postalCode at root level
      this.form.patchValue({
        city: addressData.city || '',
        postalCode: addressData.postalCode || ''
      });
    });
  }

  get f() {
    return this.form.controls;
  }

  get petsArray(): FormArray {
    return this.form.get('pets') as FormArray;
  }

  addPet() {
    this.petsArray.push(this.fb.control(''));
  }

  removePet(index: number) {
    this.petsArray.removeAt(index);
  }

  getAdditionalInfoArray(field: string): FormArray {
    return this.form.get(`additionalInformation.${field}`) as FormArray;
  }

  toggleAdditionalInfo(field: string, value: string) {
    const array = this.getAdditionalInfoArray(field);
    const index = array.controls.findIndex(control => control.value === value);
    if (index >= 0) {
      array.removeAt(index);
    } else {
      array.push(this.fb.control(value));
    }
  }

  isAdditionalInfoSelected(field: string, value: string): boolean {
    const array = this.getAdditionalInfoArray(field);
    return array.controls.some(control => control.value === value);
  }

  onSubmit() {
    this.submitted = true;

    if (this.form.invalid) {
      Swal.fire('Error', 'Please fill in all required fields.', 'error');
      return;
    }

    // Validate email or contact number
    if (!this.form.get('email')?.value && !this.form.get('contactNumber')?.value) {
      Swal.fire('Error', 'Please provide either email or contact number.', 'error');
      return;
    }

    // Validate location
    const addressValue = this.form.get('address')?.value;
    if (!addressValue?.location?.coordinates || addressValue.location.coordinates.length !== 2) {
      Swal.fire('Error', 'Please select a location from Google suggestions.', 'error');
      return;
    }

    // Build form data according to backend schema
    const additionalInfoValue = this.form.get('additionalInformation')?.value || {};
    
    // Convert additionalInformation FormArrays to regular arrays
    const additionalInformation: any = {};
    Object.keys(additionalInfoValue).forEach(key => {
      const formArray = this.getAdditionalInfoArray(key);
      additionalInformation[key] = formArray.value;
    });

    const formData = {
      sellStatus: this.form.get('sellStatus')?.value,
      propertyType: this.form.get('propertyType')?.value,
      housingType: this.form.get('housingType')?.value || null,
      roomTitle: this.form.get('roomTitle')?.value,
      bedRoomCount: this.form.get('bedRoomCount')?.value || 0,
      washRoomCount: this.form.get('washRoomCount')?.value || 0,
      propertyArea: this.form.get('propertyArea')?.value || 0,
      propertyUnitArea: this.form.get('propertyUnitArea')?.value || null,
      lotLandArea: this.form.get('lotLandArea')?.value || 0,
      lotUnitArea: this.form.get('lotUnitArea')?.value || null,
      price: this.form.get('price')?.value || '',
      monthlyRent: this.form.get('monthlyRent')?.value || 0,
      weeklyRent: this.form.get('weeklyRent')?.value || 0,
      duration: this.form.get('duration')?.value || 0,
      availableDate: this.form.get('availableDate')?.value,
      rentalPeriod: this.form.get('rentalPeriod')?.value || null,
      openHouseInfo: this.form.get('openHouseInfo')?.value || null,
      description: this.form.get('description')?.value || '',
      contactNumber: this.form.get('contactNumber')?.value,
      contactInstruction: this.form.get('contactInstruction')?.value || '',
      name: this.form.get('name')?.value || '',
      ravelId: this.form.get('ravelId')?.value || '',
      status: this.form.get('status')?.value || 'Active',
      isAvailable: this.form.get('isAvailable')?.value !== false,
      isShowProfile: this.form.get('isShowProfile')?.value ? 1 : 0,
      isFavorite: this.form.get('isFavorite')?.value || false,
      defaultImage: this.form.get('defaultImage')?.value || -1,
      images: this.form.get('images')?.value || [],
      pets: this.petsArray.value.filter((pet: string) => pet && pet !== ''),
      city: this.form.get('city')?.value || addressValue?.city || '',
      postalCode: this.form.get('postalCode')?.value || addressValue?.postalCode || '',
      address: addressValue,
      additionalInformation: additionalInformation
    };

    // Add email if provided
    if (this.form.get('email')?.value) {
      formData['email'] = this.form.get('email')?.value;
    }

    // Add housingPostId if in edit mode
    if (this.isEditMode && this.editingPostId) {
      formData['housingPostId'] = this.editingPostId;
    }

    this.ngxService.start();
    
    if (this.isEditMode && this.editingPostId) {
      // Update existing housing post
      this.authenticationService.updateHousingPost(formData).subscribe(
        (response: any) => {
          this.ngxService.stop();
          const message = response?.message || 'Housing post updated successfully';
          Swal.fire('Success', message, 'success').then(() => {
            this.resetForm();
            this.getHousingPosts();
          });
        },
        (error) => {
          this.ngxService.stop();
          const errorMessage = error.error?.message || error.error?.error || 'Failed to update housing post.';
          Swal.fire('Error', errorMessage, 'error');
        }
      );
    } else {
      // Create new housing post
      this.authenticationService.addHousingPost(formData).subscribe(
        (response: any) => {
          this.ngxService.stop();
          const message = response?.message || 'Housing post created successfully';
          Swal.fire('Success', message, 'success').then(() => {
            this.resetForm();
            this.getHousingPosts();
          });
        },
        (error) => {
          this.ngxService.stop();
          const errorMessage = error.error?.message || error.error?.error || 'Failed to create housing post.';
          Swal.fire('Error', errorMessage, 'error');
        }
      );
    }
  }

  cancel() {
    this.resetForm();
  }

  resetForm() {
    this.showCreateForm = false;
    this.submitted = false;
    this.isEditMode = false;
    this.editingPostId = null;
    this.hasGoogleSelection = false;
    if (this.form) {
      this.form.reset();
      this.initializeForm();
    }
  }

  editPost(post: any) {
    this.isEditMode = true;
    this.editingPostId = post._id || post.id;
    this.showCreateForm = true;
    this.populateFormWithPostData(post);
  }

  populateFormWithPostData(post: any) {
    if (!post || !this.form) return;

    // Populate basic fields
    this.form.patchValue({
      sellStatus: post.sellStatus || 'Sale',
      propertyType: post.propertyType || 'Apartment/Flat',
      housingType: post.housingType || '',
      roomTitle: post.roomTitle || '',
      bedRoomCount: post.bedRoomCount || 0,
      washRoomCount: post.washRoomCount || 0,
      propertyArea: post.propertyArea || 0,
      propertyUnitArea: post.propertyUnitArea || 'sq ft',
      lotLandArea: post.lotLandArea || 0,
      lotUnitArea: post.lotUnitArea || 'sq ft',
      price: post.price || '',
      monthlyRent: post.monthlyRent || 0,
      weeklyRent: post.weeklyRent || 0,
      duration: post.duration || 0,
      availableDate: post.availableDate ? (typeof post.availableDate === 'string' ? post.availableDate.split('T')[0] : new Date(post.availableDate).toISOString().split('T')[0]) : new Date().toISOString().split('T')[0],
      rentalPeriod: post.rentalPeriod || '',
      openHouseInfo: post.openHouseInfo || '',
      description: post.description || '',
      contactNumber: post.contactNumber || '',
      contactInstruction: post.contactInstruction || '',
      email: post.email || '',
      name: post.name || '',
      ravelId: post.ravelId || '',
      status: post.status || 'Active',
      isAvailable: post.isAvailable !== false,
      isShowProfile: post.isShowProfile || false,
      isFavorite: post.isFavorite || false,
      defaultImage: post.defaultImage || -1,
      city: post.city || '',
      postalCode: post.postalCode || ''
    });

    // Populate address
    if (post.address) {
      const address = post.address;
      if (address.location && address.location.coordinates && address.location.coordinates.length === 2) {
        this.hasGoogleSelection = true;
        this.form.patchValue({
          address: {
            googleAddress: address.googleAddress || address.streetAddress || '',
            streetAddress: address.streetAddress || '',
            landMark: address.landMark || '',
            city: address.city || '',
            state: address.state || '',
            postalCode: address.postalCode || '',
            country: address.country || '',
            contactNo: address.contactNo || '',
            alternateNo: address.alternateNo || '',
            location: {
              type: 'Point',
              coordinates: address.location.coordinates
            }
          }
        });
        
        // Also update city and postalCode at root level
        if (address.city) {
          this.form.patchValue({ city: address.city });
        }
        if (address.postalCode) {
          this.form.patchValue({ postalCode: address.postalCode });
        }
      }
    }

    // Populate location if available (for backward compatibility)
    if (post.location && post.location.coordinates && post.location.coordinates.length === 2) {
      this.hasGoogleSelection = true;
      if (!this.form.get('address.location.coordinates')?.value || this.form.get('address.location.coordinates')?.value.length !== 2) {
        this.form.patchValue({
          address: {
            ...this.form.get('address')?.value,
            location: {
              type: 'Point',
              coordinates: post.location.coordinates
            }
          }
        });
      }
    }

    // Populate pets array
    const petsArray = this.petsArray;
    while (petsArray.length !== 0) {
      petsArray.removeAt(0);
    }
    if (post.pets && Array.isArray(post.pets)) {
      post.pets.forEach((pet: string) => {
        if (pet) {
          petsArray.push(this.fb.control(pet));
        }
      });
    }

    // Populate images array
    const imagesArray = this.form.get('images') as FormArray;
    while (imagesArray.length !== 0) {
      imagesArray.removeAt(0);
    }
    if (post.images && Array.isArray(post.images)) {
      post.images.forEach((image: string) => {
        if (image) {
          imagesArray.push(this.fb.control(image));
        }
      });
    }

    // Populate additional information
    if (post.additionalInformation) {
      const additionalInfo = post.additionalInformation;
      Object.keys(additionalInfo).forEach(key => {
        const array = this.getAdditionalInfoArray(key);
        while (array.length !== 0) {
          array.removeAt(0);
        }
        if (Array.isArray(additionalInfo[key])) {
          additionalInfo[key].forEach((value: string) => {
            if (value) {
              array.push(this.fb.control(value));
            }
          });
        }
      });
    }
  }

  getHousingPosts() {
    this.ngxService.start();
    this.authenticationService.getMyHousingPosts().subscribe(
      (response: any) => {
        console.log('API Response:', response);
        if (Array.isArray(response)) {
          this.housingPosts = response;
        } else if (Array.isArray(response?.items)) {
          this.housingPosts = response.items;
        } else if (Array.isArray(response?.data)) {
          this.housingPosts = response.data;
        } else {
          this.housingPosts = [];
        }
        console.log('Housing posts array:', this.housingPosts);
        this.page = 1;
        this.ngxService.stop();
      },
      (error) => {
        this.ngxService.stop();
        console.error('Error loading housing posts:', error);
        Swal.fire('Error', error.error?.message || 'Failed to load housing posts.', 'error');
      }
    );
  }

  toggleCreateForm() {
    this.showCreateForm = !this.showCreateForm;
    if (this.showCreateForm && !this.form) {
      this.initializeForm();
    }
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.housingPosts?.length || 0) / this.pageSize));
  }

  get pagedPosts(): any[] {
    const start = (Math.max(1, this.page) - 1) * this.pageSize;
    return (this.housingPosts || []).slice(start, start + this.pageSize);
  }

  prevPage(): void {
    if (this.page > 1) this.page -= 1;
  }

  nextPage(): void {
    if (this.page < this.totalPages) this.page += 1;
  }

  formatDate(date: any): string {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY');
  }

  isPostExpired(activeTill: any): boolean {
    if (!activeTill) return false;
    return moment(activeTill).isBefore(moment());
  }

  getPropertyLocation(post: any): string {
    if (post?.address?.streetAddress) {
      return post.address.streetAddress;
    }
    if (post?.address?.city) {
      return post.address.city;
    }
    if (post?.city) {
      return post.city;
    }
    return '-';
  }

  openPostDetails(content: any, post: any) {
    this.selectedPost = post;
    this.modalService.open(content, { 
      ariaLabelledBy: 'post-details-modal',
      size: 'xl',
      windowClass: 'post-details-modal',
      centered: true
    });
  }

  getFullAddress(post: any): string {
    if (post?.address) {
      const addr = post.address;
      const parts = [
        addr.streetAddress,
        addr.city,
        addr.state,
        addr.postalCode,
        addr.country
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : '-';
    }
    return '-';
  }
}
